import type {
    Observer,
    Subject
} from '@naturehouse/nh-essentials/lib/architecture/ObserverPattern';
import captureSafeException from '../../../../util/sentry';
import ReviewListItems from './ReviewListItems';
import ReviewPaginationRenderer from './ReviewPaginationRenderer';

export default class ReviewPaginationHandler implements Observer {
    static #instance: ReviewPaginationHandler | null = null;

    static getInstance(): ReviewPaginationHandler {
        if (!ReviewPaginationHandler.#instance) {
            ReviewPaginationHandler.#instance = new ReviewPaginationHandler();
        }

        return ReviewPaginationHandler.#instance;
    }

    #paginationElement: HTMLElement | null = null;

    readonly #reviewPaginationRenderer: Promise<ReviewPaginationRenderer> =
        ReviewPaginationRenderer.getInstance();

    readonly #reviewListItems: ReviewListItems = ReviewListItems.getInstance();

    public constructor() {
        this.#reviewPaginationRenderer
            .then((renderer) => {
                renderer.attach(this);
            })
            .catch((err) => {
                captureSafeException(err, 'review_pagination_renderer_init_failed', null, {
                    region: 'ReviewPaginationHandler',
                    feature: 'reviews'
                });
            });
    }

    public update(subject: Subject): void {
        if (!(subject instanceof ReviewPaginationRenderer)) {
            return;
        }

        this.#paginationElement = subject.paginationElement;
        this.#setEventListeners();
    }

    #setEventListeners(): void {
        this.#paginationElement?.addEventListener('click', this.#handlePaginationClick);
    }

    readonly #handlePaginationClick = (event: Event): void => {
        event.preventDefault();

        const link = (event.target as HTMLElement).closest('a');

        if (!link || !link.hash) {
            return;
        }

        const page = parseInt(link.hash.slice(1), 10);

        if (Number.isNaN(page) || page < 1) {
            return;
        }

        this.#reviewListItems.goToPage(page);

        requestAnimationFrame((): void => {
            const container = this.#paginationElement?.closest('[data-role="reviews-container"]');
            const contentContainer = this.#paginationElement?.closest('[data-role="content"]');
            const firstReview = container?.querySelector('li');

            if (!firstReview || !contentContainer) {
                return;
            }

            const stickyHeight = contentContainer.getBoundingClientRect().height + 4 || 0;
            firstReview.style.scrollMarginTop = `${stickyHeight}px`;
            firstReview.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        });
    };
}
